import type { GitCommit } from "@foreman/protocol";

/** Where one commit's row draws its lines. Lanes are columns, counted from the left. */
export interface GraphRow {
  lane: number;
  /** Lines coming in from the row above: to this commit's dot (`to` is its lane) or straight through. */
  top: { lane: number; to: number }[];
  /** Lines going out to the row below: from this commit's dot (`from` is its lane) or straight through. */
  bottom: { lane: number; from: number }[];
}

/**
 * Lays commits (newest first, children before parents, as `git log --topo-order` gives them) out in lanes, the way
 * VS Code's graph does: the first parent keeps the commit's lane, other parents of a merge open or join a lane.
 */
export function layoutGraph(input: Pick<GitCommit, "hash" | "parents">[]): { rows: GraphRow[]; lanes: number } {
  // An older computer doesn't say who each commit's parents are. Without them every row would be a bare dot, so draw
  // one straight line (each commit followed by the next older one) until it is updated.
  const commits = input.every((c) => c.parents === undefined) ? input.map((c, i) => ({ hash: c.hash, parents: input[i + 1] ? [input[i + 1]!.hash] : [] })) : input;
  let active: (string | null)[] = []; // the commit each lane is waiting for
  const rows: GraphRow[] = [];
  let width = 1;
  for (const c of commits) {
    const before = active.slice();
    let lane = before.indexOf(c.hash);
    if (lane < 0) {
      lane = before.indexOf(null);
      if (lane < 0) lane = before.length;
    }
    const top: GraphRow["top"] = [];
    const bottom: GraphRow["bottom"] = [];
    const next = before.slice();
    while (next.length <= lane) next.push(null);
    // every lane waiting for this commit ends at its dot; the others run straight past
    before.forEach((h, i) => {
      if (h === null) return;
      top.push({ lane: i, to: h === c.hash ? lane : i });
      if (h === c.hash) next[i] = null;
      else bottom.push({ lane: i, from: i });
    });
    (c.parents ?? []).forEach((p, k) => {
      let at = next.indexOf(p);
      if (at < 0) {
        at = k === 0 ? lane : next.indexOf(null);
        if (at < 0) at = next.length;
        while (next.length <= at) next.push(null);
        next[at] = p;
      }
      bottom.push({ lane: at, from: lane });
    });
    while (next.length && next[next.length - 1] === null) next.pop();
    active = next;
    width = Math.max(width, lane + 1, ...top.map((t) => t.lane + 1), ...bottom.map((b) => b.lane + 1));
    rows.push({ lane, top, bottom });
  }
  return { rows, lanes: width };
}
